import { Component, ViewChild } from '@angular/core';
import { InfractionsService } from './infractions.service';
import { NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { AuthService } from 'src/app/shared/services/auth.service';
import { firstValueFrom } from 'rxjs';
import { TableInfractionsComponent } from '../table-infractions/table-infractions.component';

@Component({
  selector: 'app-infractions',
  templateUrl: './infractions.component.html',
  styleUrls: ['./infractions.component.scss'],
})
export class InfractionsComponent {
  valueMedPix: number = 0;
  qtdeMedPix: number = 0;
  valueComplaint: number = 0;
  qtdeComplaint: number = 0;
  valueJudicial: number = 0;
  qtdeJudicial: number = 0;

  extrato = new FormGroup({
    tipo: new FormControl<string>(''),
    status: new FormControl<string>('REALIZADO'),
    tipoBusca: new FormControl<string>(''),
    origem: new FormControl<string>(''),
    responsavel: new FormControl<string>(''),
    initialDate: new FormControl<Date | null>(
      null,
      { validators: [Validators.required], updateOn: 'blur' }
    ),
    endDate: new FormControl<Date | null>(
      null,
      { validators: [Validators.required], updateOn: 'blur' }
    ),
  });

  transactionTypes = [
    { name: 'common.all', value: '' },
    { name: 'Medpix', value: 'Medpix' },
    { name: 'Reclamação', value: 'Reclamação' },
    { name: 'Judicial', value: 'Judicial' }
  ];

  statusList: any[] = [
    { name: 'Todos', value: '' },
    { name: 'Analise', value: 'ANALISE' },
    { name: 'Cancelado', value: 'CANCELADO' },
    { name: 'Reembolso ', value: 'Reembolso ' },
  ];

  tipoBuscaList: any[] = [
    { name: 'common.userName', value: 'personName' },
    { name: 'common.transactionId', value: 'id' },
    { name: 'common.endToEndId', value: 'idempotencyKey' },
    { name: 'common.document', value: 'cpf' },
  ];

  public isFilterColapsed = false;
  public isSubmitted = false;
  public isLoaded: boolean = false;
  public useThreeDecimals = false;

  private accountTimezoneOffsetMinutes: number | null = null;
  private originCurrency: string = 'BRL';

  @ViewChild(TableInfractionsComponent, { static: true }) table!: TableInfractionsComponent;

  constructor(
    private infractionsService: InfractionsService,
    private authService: AuthService,
    private toastr: ToastrService
  ) {
    this.extrato.controls.status.setValue('');
    this.extrato.controls.tipoBusca.setValue('personName');
  }

  private parseTimezoneOffset(tz: string | null | undefined): number | null {
    if (!tz) return null;

    const m = /^([+-]?)(\d{1,2}):(\d{2})$/.exec(tz.trim());
    if (!m) return null;

    const sign = m[1] === '-' ? 1 : -1;
    const hours = Number(m[2]);
    const minutes = Number(m[3]);

    return sign * (hours * 60 + minutes);
  }

  private nowInAccountTz(): Date {
    const now = new Date();

    if (this.accountTimezoneOffsetMinutes == null) {
      return now;
    }

    const browserOffset = now.getTimezoneOffset();
    const diffMinutes = this.accountTimezoneOffsetMinutes - browserOffset;

    return new Date(now.getTime() - diffMinutes * 60000);
  }

  private buildDefaultRange(): { start: Date; end: Date } {
    const origin = (this.originCurrency || 'BRL').toUpperCase();

    if (origin === 'BRL') {
      const now = new Date();

      const start = new Date(now);
      start.setDate(start.getDate() - 15);
      start.setHours(0, 0, 0, 0);

      const end = new Date(now);
      end.setHours(23, 59, 59, 999);

      return { start, end };
    }

    const nowAcc = this.nowInAccountTz();

    const start = new Date(nowAcc);
    start.setDate(start.getDate() - 15);
    start.setHours(0, 0, 0, 0);

    const end = new Date(nowAcc);
    end.setHours(23, 59, 59, 999);

    return { start, end };
  }

  async ngOnInit(): Promise<void> {
    try {
      const account = await firstValueFrom(this.authService.getAccount());
      const hasFlag = !!account?.featureFlagNewParam;

      this.useThreeDecimals = hasFlag;

      this.originCurrency =
        ((account as any)?.originCurrency ||
          this.authService.getOriginCurrency?.() ||
          'BRL').toUpperCase();

      this.accountTimezoneOffsetMinutes = this.parseTimezoneOffset(
        (account as any)?.timezone
      );

      const { start, end } = this.buildDefaultRange();

      this.extrato.controls.initialDate.setValue(start);
      this.extrato.controls.endDate.setValue(end);

      this.applyToTableAndFetch(start, end);
      this.loadScreen();
    } catch (e) {
      const now = new Date();
      const start = new Date(now);
      start.setHours(0, 0, 0, 0);
      this.extrato.controls.initialDate.setValue(start);
      this.extrato.controls.endDate.setValue(now);
      this.loadScreen();
    }
  }

  private formatSeconds(v: any): Date {
    const d = this.asDate(v);
    const copy = new Date(d);
    copy.setSeconds(59, 999);
    return copy;
  }

  private toLocalIso(v: any): string {
    const dt = this.asDate(v);
    const pad = (n: number) => String(n).padStart(2, '0');
    const y = dt.getFullYear();
    const m = pad(dt.getMonth() + 1);
    const d = pad(dt.getDate());
    const hh = pad(dt.getHours());
    const mm = pad(dt.getMinutes());
    const ss = pad(dt.getSeconds());
    return `${y}-${m}-${d}T${hh}:${mm}:${ss}`;
  }

  private toNgb(v: any): NgbDateStruct {
    const d = this.asDate(v);
    return { year: d.getFullYear(), month: d.getMonth() + 1, day: d.getDate() };
  }

  private asDate(v: any): Date {
    return v?.toDate ? v.toDate() : (v as Date);
  }

  private applyToTableAndFetch(start: Date, tempEnd: Date) {
    this.table.page = 1;

    const startDay = this.startOfDay(start);
    const endDay = this.endOfDay(tempEnd);

    this.table.dataInitial = this.toNgb(startDay);
    this.table.dataFinal = this.toNgb(endDay);

    this.table.startDateIso = this.toLocalIso(startDay);
    this.table.endDateIso = this.toLocalIso(endDay);

    this.table.selectedStatus = this.extrato.controls.status.value ?? '';
    this.table.transactionType = this.extrato.controls.tipo.value ?? '';
    this.table.personName = this.extrato.controls.responsavel.value ?? '';
    this.table.searchType = this.extrato.controls.tipoBusca.value ?? '';

    this.table.getTransactions();
  }

  loadScreen() {
    const start = this.startOfDay(this.extrato.controls.initialDate.value!);
    const end = this.endOfDay(this.extrato.controls.endDate.value!);

    const dateInitialFormat = this.toLocalIso(start);
    const dateFinalFormat = this.toLocalIso(end);

    const personName = this.extrato.controls.responsavel.value ?? '';
    const searchType = this.extrato.controls.tipoBusca.value ?? '';

    this.infractionsService
      .getDashBoardExtratoValues(dateInitialFormat, dateFinalFormat, personName, searchType)
      .subscribe(v => {
        this.valueMedPix = v.valueMedPix;
        this.qtdeMedPix = v.qtdeMedPix;
        this.valueComplaint = v.valueComplaint;
        this.qtdeComplaint = v.qtdeComplaint;
        this.valueJudicial = v.valueJudicial;
        this.qtdeJudicial = v.qtdeJudicial;
        this.isLoaded = true;
      });
  }

  async paste(): Promise<void> {
    try {
      const text = await navigator.clipboard.readText();
      this.extrato.controls.responsavel.setValue(text.trim());
    } catch (err) {
      console.error('Erro ao colar o código:', err);
    }
  }

  buscar() {
    this.isSubmitted = true;
    this.isLoaded = false;

    const start = this.extrato.controls.initialDate.value!;
    const end = this.extrato.controls.endDate.value!;

    if (start > end) {
      this.toastr.error('Data inicial deve ser menor ou igual a data final!', 'Erro', { timeOut: 3000, positionClass: 'toast-top-right' });
      this.isLoaded = true;
      return;
    }

    if (this.extrato.invalid) {
      this.isLoaded = true;
      return;
    }

    const startDay = this.startOfDay(start);
    const endDay = this.endOfDay(end);

    this.table.page = 1;
    this.table.dataInitial = this.toNgb(startDay);
    this.table.dataFinal = this.toNgb(endDay);

    this.table.transactionType = this.extrato.controls.tipo.value ?? '';
    this.table.selectedStatus = this.extrato.controls.status.value ?? '';
    this.table.personName = this.extrato.controls.responsavel.value ?? '';
    this.table.searchType = this.extrato.controls.tipoBusca.value ?? '';

    this.table.startDateIso = this.toLocalIso(startDay);
    this.table.endDateIso = this.toLocalIso(endDay);

    this.table.getTransactions();
    this.loadScreen();
    this.isSubmitted = false;
  }

  filterColapse() {
    this.isFilterColapsed = !this.isFilterColapsed;
  }

  private parseDate(s: string): Date | null {
    if (!s) return null;
    const m = s.trim().match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (!m) return null;
    const d = new Date(+m[3], +m[2] - 1, +m[1], 0, 0, 0, 0);
    return isNaN(d.getTime()) ? null : d;
  }

  parseDateInput(ctrlName: 'initialDate' | 'endDate', raw: string) {
    const ctrl = this.extrato.controls[ctrlName];
    const v = ctrl.value as any;
    if (v instanceof Date && !isNaN(v.getTime())) return;

    const parsed = this.parseDate(raw);
    if (parsed) {
      ctrl.setValue(parsed, { emitEvent: false });
      ctrl.setErrors(null);
    } else if (raw?.trim()) {
      ctrl.setErrors({ matDatepickerParse: { text: raw } });
    } else {
      ctrl.setValue(null, { emitEvent: false });
      ctrl.setErrors({ required: true });
    }
  }

  private startOfDay(d: Date): Date {
    const x = new Date(d);
    x.setHours(0, 0, 0, 0);
    return x;
  }

  private endOfDay(d: Date): Date {
    const x = new Date(d);
    x.setHours(23, 59, 59, 999);
    return x;
  }
}
